import { DictType } from "@pma/types"
import { useAuth } from "@stores/auth-store"
import { toast } from "@hooks/use-toast"
import { SERVER_URL, WORK_DIARY_EDIT_ENDPOINT } from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { cn } from "@pma/utils/helpers"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { parseCookies } from "nookies"
import { ReactNode, useEffect, useState } from "react"
import GeoCoder from "./geo-coder"
import StatusEditor from "./project-status-editor"
import { Textarea } from "@components/common/textarea"
import NeighborhoodSelector from "./neighbourhood-selector"
import { Edit2 } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import DateEditor from "./date-editor"

interface Props {
  id: any
  title: string
  children: ReactNode
  value: string | null | undefined
  refetch?: CallableFunction
  type?: "address" | "status" | "neighborhood" | "date"
  maxLength?: number
  root_folder?: DictType
  setIsLoading?: (loading: boolean) => void
  workDiaryDetails: DictType
}
const WorkDiariesEditor = ({
  children,
  id,
  title,
  value,
  type,
  maxLength,
  setIsLoading,
  workDiaryDetails,
}: Props) => {
  const { authConfig } = useAuth()
  const queryClient = useQueryClient()
  const { translate } = useTranslator()

  const userId = authConfig?.id
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [hover, setHover] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [geoLocation, setGeoLocation] = useState<any>()
  const [updatedValue, setUpdatedValue] = useState<any>(value ?? "")

  const updateProjectDetails = async () => {
    let payload = {
      diary_id: id,
      user_id: userId,
      project_id: workDiaryDetails?.project_id,
      date: workDiaryDetails?.diary_date,
      weather_id: workDiaryDetails?.weather_id,
      contractor_id: workDiaryDetails?.contractor_id, // need to make dynamic
      status_id: workDiaryDetails?.status_id,
      temperature: workDiaryDetails?.temperature,
      weather_rating: workDiaryDetails?.weather_rating,
      description: workDiaryDetails?.description,
      work_details: workDiaryDetails?._work_details,
    }

    let updates

    if (type === "address") {
      updates = {
        [title]: geoLocation?.name,
        point_lng: geoLocation?.long,
        point_lat: geoLocation?.lat,
      }
    } else if (type === "neighborhood") {
      updates = {
        neighborhood_id: updatedValue,
      }
    } else {
      updates = {
        [title]: updatedValue,
      }
    }

    const response = await axios.post(
      SERVER_URL + WORK_DIARY_EDIT_ENDPOINT,
      { ...payload, ...updates },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )

    return response.data
  }

  const updateProjectDetailsMutation = useMutation({
    mutationKey: [`${title}`, id],
    mutationFn: updateProjectDetails,
    onMutate: () => {
      setEditMode(false)
      if (type === "neighborhood") {
        setIsLoading?.(true)
      }
    },
    onSuccess: (data) => {
      // Revalidate project details
      if (type === "neighborhood") setIsLoading?.(false)

      queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
      toast({
        title: translate("Success"),
        description: translate("Address field updated successfully"),
        variant: "success",
      })
    },
    onError: (error) => {
      if (type === "neighborhood") {
        setIsLoading?.(false)
      }
      toast({
        title: translate("Error"),
        description: `${translate(`Error occurred while updating`)} ${title.replace("_", " ")}`,
        variant: "error",
      })
    },
  })

  const onSubmit = () => {
    setEditMode(false)
    if (updatedValue && updatedValue !== value) {
      // Only update when the content is updated
      updateProjectDetailsMutation.mutate()
    }
    return
  }

  let Component
  switch (type) {
    case "address":
      Component = <GeoCoder location={geoLocation?.name || ""} setGeoLocation={setGeoLocation} />
      break
    case "status":
      Component = (
        <StatusEditor status={{ name: value }} setStatus={setUpdatedValue} projectId={id} />
      )
      break

    case "date":
      const projectDates = value as string
      const startAt = projectDates?.split("_")[0]
      const finishTo = projectDates?.split("_")[1]

      Component = (
        <DateEditor
          value={{
            from: parseInt(startAt),
            to: parseInt(finishTo),
          }}
          projectId={id}
        />
      )
      break
    case "neighborhood":
      Component = (
        <NeighborhoodSelector
          value={updatedValue}
          setValue={setUpdatedValue}
          projectId={id}
          setEditMode={setEditMode}
        />
      )
      break
    default:
      Component = (
        <div className='flex flex-col'>
          <input
            className='mb-1 rounded border bg-white/20 px-2 font-normal outline-none focus:border-primary'
            value={updatedValue}
            onChange={(e) => setUpdatedValue(e?.target?.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key !== "Enter") return
              onSubmit()
            }}
          />
        </div>
      )
  }

  useEffect(() => {
    if (value) setUpdatedValue(value)

    return () => {
      setUpdatedValue("")
    }
  }, [value])

  useEffect(() => {
    if (geoLocation?.name) updateProjectDetailsMutation.mutate()
  }, [geoLocation?.name])

  return (
    <div
      className={cn(
        "hover:border-3 flex w-full cursor-pointer items-center justify-start gap-2 text-center font-semibold text-black hover:border-primary",
        updateProjectDetailsMutation.isPending &&
          "animate-shimmer bg-gradient-to-r from-black to-slate-500 bg-clip-text font-light text-transparent",
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {!editMode && (
        <div className='flex h-full w-full gap-2 text-left'>
          {children}
          <Edit2
            className={cn(
              "edit-icon hover:border-3 text-primary",
              hover ? "opacity-100" : "opacity-0",
            )}
            size={14}
            onClick={(e) => {
              e.stopPropagation()
              setEditMode(true)
            }}
          />
        </div>
      )}
      {editMode && <>{Component}</>}
    </div>
  )
}

export default WorkDiariesEditor
